/**
 * TanStack Query hooks for Resource Tags
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchResourceTags,
  fetchResourceTag,
  createResourceTag,
  updateResourceTag,
  deleteResourceTag,
  addResourceTagRecord,
  updateResourceTagRecord,
  deleteResourceTagRecord,
  bulkDeleteResourceTagRecords,
} from '@/lib/resource-tags-api'
import { isApiError } from '@/lib/api-client'
import type {
  ResourceTagQueryParams,
  CreateResourceTagRequest,
  UpdateResourceTagRequest,
  ResourceTagRecordInput,
  UpdateResourceTagRecordRequest,
} from '@/types/resource-tag'

export const resourceTagKeys = {
  all: ['resource-tags'] as const,
  lists: () => [...resourceTagKeys.all, 'list'] as const,
  list: (params: ResourceTagQueryParams) => [...resourceTagKeys.lists(), params] as const,
  details: () => [...resourceTagKeys.all, 'detail'] as const,
  detail: (id: string) => [...resourceTagKeys.details(), id] as const,
}

function toastApiError(error: unknown, fallback: string) {
  if (isApiError(error)) {
    toast.error(error.message || fallback)
  } else {
    toast.error(fallback)
  }
}

export function useResourceTags(params: ResourceTagQueryParams = {}) {
  return useQuery({
    queryKey: resourceTagKeys.list(params),
    queryFn: () => fetchResourceTags(params),
  })
}

export function useResourceTag(id: string | undefined) {
  return useQuery({
    queryKey: resourceTagKeys.detail(id ?? ''),
    queryFn: () => fetchResourceTag(id!),
    enabled: !!id,
  })
}

export function useCreateResourceTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateResourceTagRequest) => createResourceTag(data),
    onSuccess: (tag) => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      toast.success(`Data classification "${tag.resourceTag}" created`)
    },
    onError: (error) => toastApiError(error, 'Failed to create data classification'),
  })
}

export function useUpdateResourceTag(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateResourceTagRequest) => updateResourceTag(id, data),
    onSuccess: (tag) => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      queryClient.setQueryData(resourceTagKeys.detail(id), tag)
      toast.success('Data classification updated')
    },
    onError: (error) => toastApiError(error, 'Failed to update data classification'),
  })
}

export function useDeleteResourceTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteResourceTag(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      queryClient.removeQueries({ queryKey: resourceTagKeys.detail(id) })
      toast.success('Data classification deleted')
    },
    onError: (error) => toastApiError(error, 'Failed to delete data classification'),
  })
}

export function useAddResourceTagRecord(tagId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ResourceTagRecordInput) => addResourceTagRecord(tagId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.detail(tagId) })
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      toast.success('Record added')
    },
    onError: (error) => toastApiError(error, 'Failed to add record'),
  })
}

export function useUpdateResourceTagRecord(tagId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      recordId,
      data,
    }: {
      recordId: string
      data: UpdateResourceTagRecordRequest
    }) => updateResourceTagRecord(tagId, recordId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.detail(tagId) })
      toast.success('Record updated')
    },
    onError: (error) => toastApiError(error, 'Failed to update record'),
  })
}

export function useDeleteResourceTagRecord(tagId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (recordId: string) => deleteResourceTagRecord(tagId, recordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.detail(tagId) })
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      toast.success('Record deleted')
    },
    onError: (error) => toastApiError(error, 'Failed to delete record'),
  })
}

export function useBulkDeleteResourceTagRecords(tagId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteResourceTagRecords(tagId, { ids }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.detail(tagId) })
      queryClient.invalidateQueries({ queryKey: resourceTagKeys.lists() })
      toast.success(
        result.deleted === 1 ? '1 record deleted' : `${result.deleted} records deleted`
      )
    },
    onError: (error) => toastApiError(error, 'Failed to delete records'),
  })
}
